import { storage } from '../firebase/firebaseConfig';
import { ref, uploadBytes, uploadBytesResumable, getDownloadURL, deleteObject, listAll, getMetadata } from 'firebase/storage';

/**
 * อัพโหลดไฟล์ไปยัง Firebase Storage
 * @param {File} file - ไฟล์ที่ต้องการอัพโหลด
 * @param {string} path - path ใน storage
 * @param {object} metadata - metadata เพิ่มเติม
 * @returns {Promise<{url: string, path: string}>} - URL และ path ของไฟล์
 */
export const uploadFile = async (file, path, metadata = {}) => {
  try {
    console.log('Starting upload to path:', path);
    console.log('File size:', file.size, 'bytes');
    console.log('File type:', file.type);
    
    const storageRef = ref(storage, path);
    
    // สร้าง metadata ที่เหมาะสม
    const uploadMetadata = {
      contentType: file.type,
      cacheControl: 'public, max-age=31536000',
      customMetadata: {
        uploadedBy: 'girl',
        uploadedAt: new Date().toISOString(),
        originalName: file.name,
        ...metadata
      }
    };
    
    console.log('Uploading with metadata:', uploadMetadata);
    
    // ใช้ uploadBytes แทน uploadBytesResumable เพื่อความเสถียร
    const snapshot = await uploadBytes(storageRef, file, uploadMetadata);
    console.log('Upload completed, getting download URL...');
    
    const downloadURL = await getDownloadURL(snapshot.ref);
    console.log('Download URL obtained:', downloadURL);
    
    return {
      url: downloadURL,
      path: snapshot.ref.fullPath,
      size: file.size,
      type: file.type
    };
    
  } catch (error) {
    console.error('Error uploading file:', error);
    console.error('Error details:', {
      code: error.code,
      message: error.message,
      stack: error.stack
    });
    
    // แปลง error เป็นข้อความที่เข้าใจง่าย
    if (error.code === 'storage/unauthorized') {
      throw new Error('ไม่มีสิทธิ์ในการอัพโหลด กรุณาล็อกอินใหม่');
    } else if (error.code === 'storage/canceled') {
      throw new Error('การอัพโหลดถูกยกเลิก');
    } else if (error.code === 'storage/unknown') {
      throw new Error('เกิดข้อผิดพลาดที่ไม่ทราบสาเหตุ กรุณาลองใหม่อีกครั้ง');
    } else if (error.message.includes('CORS') || error.message.includes('cors')) {
      throw new Error('เกิดข้อผิดพลาด CORS กรุณาติดต่อแอดมิน');
    } else if (error.message.includes('network') || error.message.includes('Network') || error.message.includes('ERR_FAILED')) {
      throw new Error('เกิดข้อผิดพลาดเครือข่าย กรุณาตรวจสอบการเชื่อมต่ออินเทอร์เน็ต');
    } else if (error.message.includes('Failed to fetch') || error.message.includes('fetch')) {
      throw new Error('ไม่สามารถเชื่อมต่อกับเซิร์ฟเวอร์ได้ กรุณาตรวจสอบการเชื่อมต่ออินเทอร์เน็ต');
    }
    
    throw error;
  }
};

/**
 * ลบไฟล์จาก Firebase Storage
 * @param {string} path - path ของไฟล์ใน storage
 * @returns {Promise<void>}
 */
export const deleteFile = async (path) => {
  try {
    const fileRef = ref(storage, path);
    await deleteObject(fileRef);
  } catch (error) {
    console.error('Error deleting file:', error);
    throw error;
  }
};

/**
 * ดึงรายการไฟล์ในโฟลเดอร์
 * @param {string} folderPath - path ของโฟลเดอร์
 * @returns {Promise<Array>} - รายการไฟล์
 */
export const listFiles = async (folderPath) => {
  try {
    const folderRef = ref(storage, folderPath);
    const result = await listAll(folderRef);
    
    const files = await Promise.all(
      result.items.map(async (itemRef) => {
        const url = await getDownloadURL(itemRef);
        const metadata = await getMetadata(itemRef);
        
        return {
          name: itemRef.name,
          fullPath: itemRef.fullPath,
          url,
          size: metadata.size,
          contentType: metadata.contentType,
          timeCreated: metadata.timeCreated,
          updated: metadata.updated
        };
      })
    );
    
    return files;
  } catch (error) {
    console.error('Error listing files:', error);
    throw error;
  }
};

/**
 * สร้าง path สำหรับรูปภาพของน้อง
 * @param {string} girlId - ID ของน้อง
 * @param {string} status - สถานะรูป (pending, approved, rejected)
 * @param {string} fileName - ชื่อไฟล์
 * @returns {string} - path ใน storage
 */
export const createImagePath = (girlId, status, fileName) => {
  return `girls/${girlId}/${status}/${fileName}`;
};

/**
 * สร้าง path สำหรับรูปภาพทั่วไป
 * @param {string} category - หมวดหมู่ (profile, banner, etc.)
 * @param {string} fileName - ชื่อไฟล์
 * @returns {string} - path ใน storage
 */
export const createGeneralImagePath = (category, fileName) => {
  return `general/${category}/${fileName}`;
};

/**
 * ตรวจสอบขนาดไฟล์
 * @param {File} file - ไฟล์ที่ต้องการตรวจสอบ
 * @param {number} maxSizeMB - ขนาดสูงสุด (MB)
 * @returns {boolean} - true ถ้าขนาดไฟล์ไม่เกินกำหนด
 */
export const checkFileSize = (file, maxSizeMB = 5) => {
  const maxSizeBytes = maxSizeMB * 1024 * 1024;
  return file.size <= maxSizeBytes;
};

/**
 * ตรวจสอบประเภทไฟล์
 * @param {File} file - ไฟล์ที่ต้องการตรวจสอบ
 * @param {Array} allowedTypes - ประเภทไฟล์ที่อนุญาต
 * @returns {boolean} - true ถ้าเป็นประเภทไฟล์ที่อนุญาต
 */
export const checkFileType = (file, allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp']) => {
  return allowedTypes.includes(file.type);
};

/**
 * สร้าง metadata สำหรับไฟล์
 * @param {File} file - ไฟล์
 * @param {object} customMetadata - metadata เพิ่มเติม
 * @returns {object} - metadata object
 */
export const createFileMetadata = (file, customMetadata = {}) => {
  return {
    contentType: file.type,
    size: file.size,
    name: file.name,
    uploadedAt: new Date().toISOString(),
    ...customMetadata
  };
};

/**
 * คำนวณขนาดไฟล์ที่เหมาะสมสำหรับแสดงผล
 * @param {number} bytes - ขนาดไฟล์เป็น bytes
 * @returns {string} - ขนาดไฟล์ที่อ่านได้
 */
export const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes';
  
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

/**
 * สร้าง URL สำหรับแสดงรูปภาพ
 * @param {string} path - path ของไฟล์ใน storage
 * @param {object} options - ตัวเลือกการแสดงผล
 * @returns {string} - URL สำหรับแสดงรูปภาพ
 */
export const createImageUrl = (path, options = {}) => {
  const { width, height, quality = 80 } = options;
  
  if (width || height) {
    // ใช้ Firebase Storage Transform API (ถ้ามี)
    return `https://firebasestorage.googleapis.com/v0/b/hugkan-8dd6b.firebasestorage.app/o/${encodeURIComponent(path)}?alt=media&w=${width || 0}&h=${height || 0}&q=${quality}`;
  }
  
  return `https://firebasestorage.googleapis.com/v0/b/hugkan-8dd6b.firebasestorage.app/o/${encodeURIComponent(path)}?alt=media`;
};

/**
 * ลบไฟล์เก่าที่ไม่ใช้แล้ว
 * @param {string} folderPath - path ของโฟลเดอร์
 * @param {number} daysOld - จำนวนวันที่ถือว่าเก่า
 * @returns {Promise<number>} - จำนวนไฟล์ที่ลบ
 */
export const cleanupOldFiles = async (folderPath, daysOld = 14) => {
  try {
    const files = await listFiles(folderPath);
    const cutoffDate = new Date();
    cutoffDate.setDate(cutoffDate.getDate() - daysOld);
    
    let deletedCount = 0;
    
    for (const file of files) {
      const fileDate = new Date(file.timeCreated);
      if (fileDate < cutoffDate) {
        await deleteFile(file.fullPath);
        deletedCount++;
      }
    }
    
    return deletedCount;
  } catch (error) {
    console.error('Error cleaning up old files:', error);
    throw error;
  }
};

/**
 * ตรวจสอบการเชื่อมต่อ Firebase Storage
 * @returns {Promise<boolean>} - true ถ้าเชื่อมต่อได้
 */
export const checkStorageConnection = async () => {
  try {
    console.log('🔍 Testing Firebase Storage connection...');
    console.log('Storage instance:', storage);
    console.log('Storage app:', storage.app);
    console.log('Storage bucket:', storage.bucket);
    console.log('Storage app name:', storage.app.name);
    console.log('Storage app options:', storage.app.options);
    console.log('Storage app config:', storage.app.config);
    
    // ทดสอบการสร้าง reference
    const testRef = ref(storage, 'test/connection');
    console.log('Test reference created:', testRef);
    console.log('Test reference path:', testRef.fullPath);
    console.log('Test reference bucket:', testRef.bucket);
    
    // ทดสอบการเข้าถึง metadata (จะ fail ถ้าไม่มีไฟล์ แต่จะไม่ fail ถ้า connection มีปัญหา)
    try {
      await getMetadata(testRef);
      console.log('✅ Storage connection successful - file exists');
      return true;
    } catch (metadataError) {
      if (metadataError.code === 'storage/object-not-found') {
        console.log('✅ Storage connection successful - file not found (expected)');
        return true;
      } else {
        console.error('❌ Storage connection failed:', metadataError);
        return false;
      }
    }
  } catch (error) {
    console.error('❌ Storage connection error:', error);
    console.error('Error details:', {
      code: error.code,
      message: error.message,
      stack: error.stack
    });
    return false;
  }
};

/**
 * ทดสอบ CORS โดยตรง
 * @returns {Promise<boolean>} - true ถ้า CORS ทำงานได้
 */
export const testCORS = async () => {
  try {
    console.log('🌐 Testing CORS directly...');
    
    // ทดสอบการเรียก API โดยตรง
    const testUrl = 'https://firebasestorage.googleapis.com/v0/b/hugkan-8dd6b.firebasestorage.app/o/test%2Fconnection?alt=media';
    console.log('Test URL:', testUrl);
    
    const response = await fetch(testUrl, {
      method: 'GET',
      mode: 'cors',
      headers: {
        'Content-Type': 'application/json',
      }
    });
    
    console.log('CORS test response:', response);
    console.log('CORS test status:', response.status);
    console.log('CORS test headers:', response.headers);
    
    if (response.status === 404) {
      console.log('✅ CORS working - 404 is expected for non-existent file');
      return true;
    } else if (response.ok) {
      console.log('✅ CORS working - file exists');
      return true;
    } else {
      console.log('❌ CORS failed - unexpected status:', response.status);
      return false;
    }
    
  } catch (error) {
    console.error('❌ CORS test failed:', error);
    console.error('Error details:', {
      name: error.name,
      message: error.message,
      stack: error.stack
    });
    return false;
  }
};

/**
 * ทดสอบการอัพโหลดไฟล์เล็กๆ
 * @returns {Promise<boolean>} - true ถ้าอัพโหลดสำเร็จ
 */
export const testStorageUpload = async () => {
  try {
    console.log('🧪 Testing Firebase Storage upload...');
    
    // สร้างไฟล์ทดสอบเล็กๆ
    const testContent = 'Hello Firebase Storage Test';
    const testBlob = new Blob([testContent], { type: 'text/plain' });
    const testFile = new File([testBlob], 'test.txt', { type: 'text/plain' });
    
    console.log('Test file created:', testFile);
    console.log('Test file size:', testFile.size, 'bytes');
    
    // สร้าง path สำหรับไฟล์ทดสอบ
    const testPath = `test/upload-test-${Date.now()}.txt`;
    console.log('Test path:', testPath);
    
    // อัพโหลดไฟล์ทดสอบ
    const testRef = ref(storage, testPath);
    console.log('Test reference:', testRef);
    
    const snapshot = await uploadBytes(testRef, testFile);
    console.log('✅ Upload successful:', snapshot);
    
    // ลบไฟล์ทดสอบ
    try {
      await deleteObject(testRef);
      console.log('✅ Test file deleted successfully');
    } catch (deleteError) {
      console.warn('⚠️ Could not delete test file:', deleteError);
    }
    
    return true;
  } catch (error) {
    console.error('❌ Upload test failed:', error);
    console.error('Error details:', {
      code: error.code,
      message: error.message,
      stack: error.stack
    });
    return false;
  }
};

/**
 * สร้าง backup ของไฟล์
 * @param {string} sourcePath - path ของไฟล์ต้นฉบับ
 * @param {string} backupPath - path ของไฟล์ backup
 * @returns {Promise<string>} - URL ของไฟล์ backup
 */
export const createBackup = async (sourcePath, backupPath) => {
  try {
    // ดึงไฟล์ต้นฉบับ
    const sourceRef = ref(storage, sourcePath);
    const sourceUrl = await getDownloadURL(sourceRef);
    
    // ดาวน์โหลดไฟล์
    const response = await fetch(sourceUrl);
    const blob = await response.blob();
    
    // อัพโหลดเป็น backup
    const backupRef = ref(storage, backupPath);
    const snapshot = await uploadBytes(backupRef, blob);
    const backupUrl = await getDownloadURL(snapshot.ref);
    
    return backupUrl;
  } catch (error) {
    console.error('Error creating backup:', error);
    throw error;
  }
};
